import { Container, Graphics, Text } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import { UI_THEMES } from "./ui_tokens"

/**
 * The countdown dial over the target of a HELD interaction -- "hold to help up" on an injured ember
 * survivor, and anything else that ever asks a player to keep the button down.
 *
 * It answers the one question a hold cannot answer for itself: how much longer. Without it the
 * player presses, nothing visible happens for two seconds, and the natural reading is that the
 * interaction is broken -- so they let go, which is exactly the thing that cancels it.
 *
 * The time it shows is the SERVER's (see HeldInteraction and Message.holdRemainingMs), never a local
 * timer started on the press. The server decides whether a hold is running at all -- close enough,
 * nobody standing over the body, refreshes still arriving -- and a ring run off the client's own
 * clock would fill up just as convincingly for a hold that had been refused before it began.
 *
 * One of these is parented to the target's world-overlay item (see WorldOverlaySurface), so it
 * travels with the entity for free and needs no per-frame position arithmetic of its own.
 */

// Geometry in world-overlay pixels. That surface is unit-scaled and pre-multiplies world
// coordinates itself, so these are screen pixels at the default zoom -- the same frame the
// nameplate and the role glyph are laid out in.
const RADIUS = 13
const RING = 3.5
const FACE_RADIUS = RADIUS + RING / 2
// Centred over the entity and lifted clear of the name plate, which is up for the whole of a hold:
// the pointer has to be ON the entity to hold it, so the hover label is always showing underneath.
//
// The x is exact rather than tuned: WorldOverlaySurface parks the item half a tile to the LEFT of
// the entity (TileSize * 0.25 * surfaceZoom), so +16 is the entity's own centre line.
const CENTER_X = 16
const CENTER_Y = -18

// How far the server's remaining time must disagree with the local run-down before the ring is
// re-anchored to it. Under this it is flight-time jitter on a 10Hz feed, and taking every message
// at its word makes the sweep stutter instead of run.
const REANCHOR_TOLERANCE_MS = 90

// The two ways a dial ends, and how long each takes. They are deliberately different animations:
// "it worked" and "you let go too early" are the only two outcomes of a hold, and the dial is where
// the player is already looking.
const COMPLETE_MS = 260
const CANCEL_MS = 130
// Remaining time at or below which a dial that stops counts as COMPLETED rather than released. One
// server message (100ms) plus its flight: the last message before the help lands still has a few
// milliseconds on the clock, and that must not read as a cancellation.
const COMPLETE_SLACK_MS = 160

const THEME = UI_THEMES.ember

type Ending = "complete" | "cancel"

export class HoldTimer extends Container {
    private dial = new Graphics()
    private label: Text
    // When the hold runs out, on THIS machine's clock. Anchored from the server's remaining time
    // (see reanchor) rather than from a server timestamp, so the two clocks never have to agree.
    private endsAt = 0
    private durationMs = 1
    private ending?: { kind: Ending; startedAt: number; progress: number }

    constructor() {
        super()
        this.position.set(CENTER_X, CENTER_Y)
        this.addChild(this.dial)
        this.label = new TextRenderer().fontSize(10).bold().color(THEME.text).render("") as Text
        this.label.anchor.set(0.5)
        this.label.roundPixels = true
        // Pinned so the glyphs are not re-rasterized -- and so do not glitch for a frame -- when
        // this is rendered into the follow-cam PiP's RenderTexture at its own resolution.
        this.label.resolution = 2 * (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1)
        this.addChild(this.label)
    }

    /** The server says a hold is under way, with this much of it left. */
    setRemaining = (remainingMs: number, durationMs: number) => {
        this.ending = undefined
        this.durationMs = Math.max(1, durationMs || remainingMs)
        this.reanchor(remainingMs)
        this.alpha = 1
        this.scale.set(1)
        this.visible = true
    }

    /**
     * The server has stopped reporting a hold on this entity. Which ending that is depends on how
     * much time the dial still had: run out means the help landed, anything more means the player
     * let go (or walked out of reach, or something stood over the body again).
     */
    stop = (now = Date.now()) => {
        if (this.ending) return
        const remaining = this.endsAt - now
        const kind: Ending = remaining <= COMPLETE_SLACK_MS ? "complete" : "cancel"
        this.ending = { kind, startedAt: now, progress: kind === "complete" ? 1 : this.sweep(now) }
    }

    /** Whether this dial still has something to draw. False once its ending has played out. */
    animate = (now = Date.now()): boolean => {
        if (this.ending) {
            const { kind, startedAt, progress } = this.ending
            const span = kind === "complete" ? COMPLETE_MS : CANCEL_MS
            const t = Math.min(1, (now - startedAt) / span)
            this.alpha = 1 - t
            if (kind === "complete") {
                // Full, and blooming outward as it goes: the ring closing is the success.
                this.scale.set(1 + t * 0.3)
                this.draw(1, "")
            } else {
                // Draining back to nothing, which is what was just thrown away.
                this.draw(progress * (1 - t), "")
            }
            if (t >= 1) {
                this.visible = false
                return false
            }
            return true
        }
        const remaining = Math.max(0, this.endsAt - now)
        this.draw(this.sweep(now), `${(remaining / 1000).toFixed(1)}s`)
        return true
    }

    /** How much of the hold is done, 0..1. */
    private sweep = (now: number): number => {
        const remaining = Math.max(0, this.endsAt - now)
        return Math.max(0, Math.min(1, 1 - remaining / this.durationMs))
    }

    private reanchor = (remainingMs: number) => {
        const endsAt = Date.now() + Math.max(0, remainingMs)
        if (Math.abs(endsAt - this.endsAt) > REANCHOR_TOLERANCE_MS) {
            this.endsAt = endsAt
        }
    }

    private draw = (progress: number, text: string) => {
        const g = this.dial
        g.clear()
        // A dark face, because this lands over whatever the entity is standing on.
        g.beginFill(0x000000, 0.25).drawCircle(0, 1.5, FACE_RADIUS).endFill()
        g.lineStyle(1, THEME.border).beginFill(THEME.fill, 0.94).drawCircle(0, 0, FACE_RADIUS).endFill()
        // The unfilled track, so how much is left is readable as a gap and not only as a number.
        g.lineStyle(RING, 0x000000, 0.35).drawCircle(0, 0, RADIUS)
        if (progress > 0) {
            const from = -Math.PI / 2
            const to = from + Math.min(1, progress) * Math.PI * 2
            // moveTo first, or pixi strokes a spoke from the centre out to where the arc starts.
            g.lineStyle(RING, THEME.accent).moveTo(0, -RADIUS).arc(0, 0, RADIUS, from, to)
            // The moving head of the sweep. It is what makes a ring that is barely started still
            // read as moving, which is the whole point in the first half-second of a hold.
            g.lineStyle(0).beginFill(THEME.text)
                .drawCircle(Math.cos(to) * RADIUS, Math.sin(to) * RADIUS, RING / 2 + 0.4).endFill()
        }
        this.label.text = text
        this.label.visible = !!text
    }
}
